import { inject, Injectable, Provider } from '@angular/core'

import { Observable, tap } from 'rxjs'

import { UserProfile, UserProfileTiView } from '@ti/contracts'
import { AuthSession } from '@ti/core/auth/services'
import { openLinkBlank } from '@ti/core/common/utils'
import { CONFIG } from '@ti/core/contracts/config'
import { USER_DIRECTORY_EVENTS } from '@ti/core/contracts/user-directory-events'
import { BaseUserManager } from '@ti/shared/user/services'
import { UserDirectoryKind, UserStoreEventType } from '@ti/shared/user/stores'

@Injectable()
export class AdminTspUserManager extends BaseUserManager {
  protected readonly userDirectory = UserDirectoryKind.TiTsp

  protected override readonly updateMessageSummaryKey = 'users.updated-message.detail'
  protected override readonly updateMessageDetailKey = ''
  protected override readonly deleteMessageSummaryKey = 'users.deleted-message.detail'
  protected override readonly deleteMessageDetailKey = ''

  protected readonly appConfig = inject(CONFIG)
  protected readonly authSession = inject(AuthSession)

  public async sendInviteEmail(user: UserProfile) {
    return this.api.ti.user.sendTspInviteEmail(user.id)
  }

  public async loginAsTspUser(user: UserProfile) {
    const idToken = await this.authSession.getIdToken(true)
    const session = await this.api.ti.user.initLoginAsSession(idToken, user.id)
    const tspAdminUrl = `http${
      this.appConfig.tspDefaultDomain?.startsWith('127.0.0.1') ? '' : 's'
    }://${this.appConfig.tspDefaultDomain}`
    openLinkBlank(`${tspAdminUrl}?sessionId=${session.sessionId}`)
  }

  public toggleFavourite$(user: UserProfile, isFavourite: boolean): Observable<UserProfileTiView> {
    return this.api.ti.user.toggleFavourite$(user.id, isFavourite).pipe(
      tap((updatedUser) =>
        this.userStoreEvents.publish({
          type: UserStoreEventType.Updated,
          directory: this.userDirectory,
          userId: updatedUser.id,
          user: updatedUser,
        }),
      ),
    )
  }

  protected async createUser() {}

  protected async updateUser() {}

  protected async deleteUser() {}

  protected async disableUserMfa(user: UserProfile) {
    return this.api.ti.user.disableTspMfa(user.tspId, user.id)
  }
}

export const provideAdminTspUserManager = (): Provider[] => {
  return [
    AdminTspUserManager,
    { provide: BaseUserManager, useExisting: AdminTspUserManager },
    { provide: USER_DIRECTORY_EVENTS, useFactory: () => inject(AdminTspUserManager).events$ },
  ]
}
